import { useEffect, useState } from 'react'
import { device } from './device.js'

export type ScanMode = 'laser' | 'manual' | 'camera'

export type DeviceModel = {
  isGalaxyJ4Plus: boolean | undefined
  isChainway: boolean
  hasScannerPerformanceIssues: boolean | undefined
  isHandheldScanner: boolean | undefined
  barcodeInputMode: ScanMode
}

const getUserAgentAsync = async (method: 'model'): Promise<string> => {
  if (navigator.userAgentData) {
    return navigator.userAgentData.getHighEntropyValues([method]).then((ua) => ua[method]?.toLocaleLowerCase() || '')
  }
  return navigator.userAgent.toLocaleLowerCase()
}

const isGalaxyJ4Plus = () => getUserAgentAsync('model').then((model) => /sm-j415fn/.test(model))
const isChainway = () => getUserAgentAsync('model').then((model) => /c66/.test(model))

const hasScannerPerformanceIssues = () =>
  Promise.all([device.isAndroid, isGalaxyJ4Plus()]).then(([platform, model]) => !!platform && model)

const isHandheldScanner = () =>
  getUserAgentAsync('model').then((model) => /eda51|ct60|tc55|tc56|c66|eda5s|ct30p|ct45p/.test(model))

// -----------------------------------------------------------------------------
// Synchronous best-effort initial values
// -----------------------------------------------------------------------------
// `useDeviceModel` originally initialised every field to `undefined` and only
// filled them in from an async `useEffect` that reads `userAgentData`. That
// caused a visible flash on components that guard on `isHandheldScanner ===
// undefined` (e.g. `<BarcodeInput>`, which renders `null` until it knows the
// device kind). To eliminate the first-render flash we derive an initial guess
// synchronously from the classic `navigator.userAgent` string and from the
// native-app-injected `window.dhl.version`. The async effect below still runs
// and can refine the values on browsers with a frozen UA (modern Chrome), so
// the accurate result is unchanged — only the initial paint is now correct on
// the devices we care about (DHL handheld scanners and non-scanner clients).
// -----------------------------------------------------------------------------

const handheldModelRegex = /eda51|ct60|tc55|tc56|c66|eda5s|ct30p|ct45p/
const galaxyJ4PlusRegex = /sm-j415fn/
const chainwayRegex = /c66/

const getSyncUserAgent = (): string => (typeof navigator === 'undefined' ? '' : navigator.userAgent.toLocaleLowerCase())

const hasDhlNativeAppBridge = (): boolean =>
  // @ts-ignore — `window.dhl` is injected by the native wrapper app.
  typeof window !== 'undefined' && !!window.dhl?.version

const getInitialDeviceModel = (): DeviceModel => {
  const ua = getSyncUserAgent()
  const galaxy = galaxyJ4PlusRegex.test(ua)
  const chainway = chainwayRegex.test(ua)
  const handheld = handheldModelRegex.test(ua) || hasDhlNativeAppBridge()
  // eslint-disable-next-line no-nested-ternary
  const barcodeInputMode: ScanMode = handheld ? 'laser' : device.isMobile ? 'camera' : 'manual'

  return {
    isGalaxyJ4Plus: galaxy,
    isChainway: chainway,
    hasScannerPerformanceIssues: device.isAndroid && galaxy,
    isHandheldScanner: handheld,
    barcodeInputMode,
  }
}

export function useDeviceModel(): DeviceModel {
  const initial = getInitialDeviceModel()
  const [galaxy, setGalaxy] = useState<boolean | undefined>(initial.isGalaxyJ4Plus)
  const [chainway, setChainway] = useState<boolean>(initial.isChainway)
  const [performance, setPerformance] = useState<boolean | undefined>(initial.hasScannerPerformanceIssues)
  const [handheld, setHandheld] = useState<boolean | undefined>(initial.isHandheldScanner)
  const [barcodeInputMode, setBarcodeInputMode] = useState<ScanMode>(initial.barcodeInputMode)

  useEffect(() => {
    // Refine the sync initial values with the more accurate `userAgentData`
    // high-entropy hints on browsers that expose them (modern Chrome freezes
    // `navigator.userAgent` so the sync guess above may miss the model).
    const detect = async () => {
      isGalaxyJ4Plus().then(setGalaxy)
      isChainway().then(setChainway)
      hasScannerPerformanceIssues().then((isPerformance) => setPerformance(isPerformance))
      isHandheldScanner().then((isHandheldFromUserAgent) => {
        const isHandheld = isHandheldFromUserAgent || hasDhlNativeAppBridge()

        setHandheld(isHandheld)
        // eslint-disable-next-line no-nested-ternary
        setBarcodeInputMode(isHandheld ? 'laser' : device.isMobile ? 'camera' : 'manual')
      })
    }

    detect()
  }, [])

  return {
    isGalaxyJ4Plus: galaxy,
    isChainway: chainway,
    hasScannerPerformanceIssues: performance,
    isHandheldScanner: handheld,
    barcodeInputMode,
  }
}
